import React, { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Table, Button, TextInput, Label, Select } from 'flowbite-react';
import { AdminCreatorType, AdminSchool, AdminStudentBrief, adminCreateSchool, adminDeleteSchool, adminGetCreatorTypes, adminGetSchoolStudents, adminGetSchools, adminUpdateSchool, adminGetSchoolGrades, adminCreateGrade, adminUpdateGrade, adminDeleteGrade, adminGetGradeSubjects, adminCreateSubject, adminUpdateSubject, adminDeleteSubject, adminAssignSchoolAdmin, adminGetSchoolAdmins, AdminUserBrief, adminGetInstitutionFields, adminGetFieldAcademicYears, AdminField, AdminAcademicYear } from '../../utils/remote';

const AdminSchools: React.FC = () => {
  const qc = useQueryClient();

  // List/search/pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');

  // Form state for create/edit
  const [form, setForm] = useState<{ name: string; creator_type: number | '' }>({ name: '', creator_type: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AdminSchool | null>(null);

  // Selected school manage view
  const [selected, setSelected] = useState<AdminSchool | null>(null);
  const [activeTab, setActiveTab] = useState<'students' | 'structure' | 'admins'>('students');
  const [studPage, setStudPage] = useState(1);
  const [studSearch, setStudSearch] = useState('');

  // Grades & subjects state (for schools)
  const [grades, setGrades] = useState<{ id: number; name: string }[]>([]);
  const [newGrade, setNewGrade] = useState('');
  const [editingGradeId, setEditingGradeId] = useState<number | null>(null);
  const [editingGradeName, setEditingGradeName] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState<number | null>(null);
  const [subjects, setSubjects] = useState<{ id: number; name: string }[]>([]);
  const [newSubject, setNewSubject] = useState('');
  const [editingSubjectId, setEditingSubjectId] = useState<number | null>(null);
  const [editingSubjectName, setEditingSubjectName] = useState('');

  // Fields & Academic Years state (for universities/colleges)
  const [fields, setFields] = useState<AdminField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [years, setYears] = useState<AdminAcademicYear[]>([]);

  // Assign/create admin state
  const [adminForm, setAdminForm] = useState({ existing_user_id: '', username: '', password: '', email: '', first_name: '', last_name: '' });
  const [admins, setAdmins] = useState<AdminUserBrief[]>([]);

  // Ensure admins are reloaded when switching schools or tab is admins
  useEffect(() => {
    let cancelled = false;
    async function loadAdmins() {
      if (selected && activeTab === 'admins') {
        try {
          const list = await adminGetSchoolAdmins(selected.id);
          if (!cancelled) setAdmins(list);
        } catch (e) {
          if (!cancelled) setAdmins([]);
        }
      }
    }
    // clear previous admins on school change
    setAdmins([]);
    loadAdmins();
    return () => { cancelled = true; };
  }, [selected?.id, activeTab]);

  const schoolsQ = useQuery({
    queryKey: ['admin-schools', page, pageSize, search],
    queryFn: () => adminGetSchools({ page, page_size: pageSize, search: search.trim() || undefined }),
    keepPreviousData: true,
  });

  const typesQ = useQuery({
    queryKey: ['admin-creator-types'],
    queryFn: adminGetCreatorTypes,
  });

  const createMut = useMutation({
    mutationFn: adminCreateSchool,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      setForm({ name: '', creator_type: '' });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<{ name: string; creator_type: number }> }) => adminUpdateSchool(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      setEditOpen(false);
      setEditing(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => adminDeleteSchool(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      if (selected && selected.id === idRef.current) setSelected(null);
    },
  });

  // Helper ref to remember last deleted id
  const idRef = React.useRef<number | null>(null);

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.creator_type) return;
    createMut.mutate({ name: form.name.trim(), creator_type: Number(form.creator_type) });
  };

  const total = schoolsQ.data?.count ?? 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const schools = schoolsQ.data?.results ?? [];

  // Students query, depends on selected school
  const studentsQ = useQuery({
    queryKey: ['admin-school-students', selected?.id, studPage, studSearch],
    queryFn: () => adminGetSchoolStudents(selected!.id, { page: studPage, page_size: 10, search: studSearch.trim() || undefined }),
    enabled: !!selected,
    keepPreviousData: true,
  });

  const types = typesQ.data || [];

  const openEdit = (s: AdminSchool) => {
    setEditing(s);
    setEditOpen(true);
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    // For simplicity, allow changing only name or creator_type
    const name = (document.getElementById('edit-name') as HTMLInputElement)?.value?.trim() || editing.name;
    const creator_typeVal = (document.getElementById('edit-creator') as HTMLSelectElement)?.value;
    const patch: any = { name };
    if (creator_typeVal) patch.creator_type = Number(creator_typeVal);
    updateMut.mutate({ id: editing.id, patch });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-black dark:text-white">Manage Schools</h1>
        <div className="text-sm text-body">{schoolsQ.isFetching ? 'Refreshing…' : ''}</div>
      </div>

      {/* Search */}
      <form onSubmit={(e)=>{e.preventDefault(); schoolsQ.refetch();}} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-4">
          <Label htmlFor="search" value="Search by name or type" />
          <TextInput id="search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="School name or type..." />
        </div>
        <div>
          <Button type="submit" className="mt-6">Search</Button>
        </div>
      </form>

      {/* Create school */}
      <form onSubmit={onCreate} className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <h2 className="mb-3 font-semibold text-black dark:text-white">Add School</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="newname" value="School name" />
            <TextInput id="newname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="School name" />
          </div>
          <div>
            <Label htmlFor="newtype" value="Type" />
            <Select id="newtype" value={form.creator_type as any} onChange={(e) => setForm({ ...form, creator_type: e.target.value ? Number(e.target.value) : '' })}>
              <option value="">Select Type</option>
              {types.map((t: AdminCreatorType) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={createMut.isPending} className="w-full">{createMut.isPending ? 'Creating…' : 'Create'}</Button>
          </div>
        </div>
        {createMut.isError && <div className="mt-2 text-sm text-red-600">Failed: {(createMut.error as any)?.response?.data ? JSON.stringify((createMut.error as any).response.data) : (createMut.error as any)?.message}</div>}
      </form>

      {/* Schools table */}
      <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="max-w-full overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>ID</Table.HeadCell>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {schoolsQ.isLoading ? (
                <Table.Row><Table.Cell colSpan={4}>Loading…</Table.Cell></Table.Row>
              ) : schools.length === 0 ? (
                <Table.Row><Table.Cell colSpan={4} className="text-center text-slate-500">No schools</Table.Cell></Table.Row>
              ) : (
                schools.map((s) => (
                  <Table.Row key={s.id}>
                    <Table.Cell>{s.id}</Table.Cell>
                    <Table.Cell className="font-medium text-slate-700 dark:text-white">{s.name}</Table.Cell>
                    <Table.Cell>{s.creator_type_name}</Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-2">
                        <Button color="light" size="xs" onClick={() => { setSelected(s); setStudPage(1); setStudSearch(''); }}>Manage</Button>
                        <Button color="light" size="xs" onClick={() => openEdit(s)}>Edit</Button>
                        <Button color="failure" size="xs" onClick={() => { if (confirm(`Delete school ${s.name}?`)) { idRef.current = s.id; deleteMut.mutate(s.id); } }}>Delete</Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm">Total: {total}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-stroke px-3 py-1 disabled:opacity-50">Prev</button>
          <span className="px-2">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded border border-stroke px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      </div>

      {/* Manage Institute Modal */}
      <Modal show={!!selected} size="6xl" onClose={() => setSelected(null)} className="z-[9999]">
        <Modal.Header>
          <div className="w-full">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Manage Institute</div>
                <div className="text-lg font-semibold text-slate-900 dark:text-white">{selected?.name}</div>
                {selected?.creator_type_name && (
                  <div className="mt-0.5 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-boxdark-2 dark:text-slate-300">{selected.creator_type_name}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <a className="text-sm text-primary hover:underline" href={`/admin/exams?institution=${selected?.id}`} onClick={(e)=>{e.preventDefault(); window.location.href=`/admin/exams`;}}>Manage Exams</a>
                <Button color="light" size="xs" onClick={()=>setSelected(null)}>Close</Button>
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Tabs */}
          <div className="mb-4 flex items-center gap-2 border-b border-slate-200 pb-2 dark:border-strokedark">
            <button
              className={`rounded-full px-3 py-1 text-sm transition ${activeTab==='students' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200' : 'hover:bg-slate-100 dark:hover:bg-boxdark-2'}`}
              onClick={() => setActiveTab('students')}
            >
              Students
              <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-white px-2 text-xs text-slate-700 dark:bg-boxdark-2 dark:text-slate-300">
                {activeTab==='students' ? (studentsQ.data?.count ?? 0) : ''}
              </span>
            </button>
            <button
              className={`rounded-full px-3 py-1 text-sm transition ${activeTab==='structure' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200' : 'hover:bg-slate-100 dark:hover:bg-boxdark-2'}`}
              onClick={async () => {
                setActiveTab('structure');
                setSelectedGradeId(null);
                setSubjects([]);
                setSelectedFieldId(null);
                setYears([]);
                if (selected) {
                  const type = (selected.creator_type_name || '').toLowerCase();
                  if (type.includes('university') || type.includes('college')) {
                    try {
                      const fs = await adminGetInstitutionFields(selected.id);
                      setFields(fs);
                    } catch { setFields([]); }
                  } else {
                    try {
                      const gr = await adminGetSchoolGrades(selected.id);
                      setGrades(gr);
                    } catch { setGrades([]); }
                  }
                }
              }}
            >
              {(() => {
                const t = (selected?.creator_type_name || '').toLowerCase();
                return (t.includes('university') || t.includes('college')) ? 'Fields & Academic Years' : 'Grades & Subjects';
              })()}
              <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-white px-2 text-xs text-slate-700 dark:bg-boxdark-2 dark:text-slate-300">
                {(() => {
                  if (activeTab==='structure') {
                    const t = (selected?.creator_type_name || '').toLowerCase();
                    return (t.includes('university') || t.includes('college')) ? fields.length : grades.length;
                  }
                  return '';
                })()}
              </span>
            </button>
            <button
              className={`rounded-full px-3 py-1 text-sm transition ${activeTab==='admins' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200' : 'hover:bg-slate-100 dark:hover:bg-boxdark-2'}`}
              onClick={async () => { setActiveTab('admins'); if (selected) { try { const list = await adminGetSchoolAdmins(selected.id); setAdmins(list); } catch {} } }}
            >
              Admin Users
              <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-white px-2 text-xs text-slate-700 dark:bg-boxdark-2 dark:text-slate-300">
                {activeTab==='admins' ? admins.length : ''}
              </span>
            </button>
            <div className="ml-auto text-xs text-slate-500">ID: {selected?.id}</div>
          </div>

          {/* Students Tab */}
          {activeTab === 'students' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end mb-3">
                <div className="md:col-span-4">
                  <Label htmlFor="studsearch" value="Search students" />
                  <TextInput id="studsearch" value={studSearch} onChange={(e) => { setStudSearch(e.target.value); setStudPage(1); }} placeholder="Search students" />
                </div>
                <div>
                  <Button onClick={() => studentsQ.refetch()} className="mt-6">Search</Button>
                </div>
              </div>
              <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                <div className="max-w-full overflow-x-auto">
                  <Table hoverable>
                    <Table.Head>
                      <Table.HeadCell>ID</Table.HeadCell>
                      <Table.HeadCell>Name</Table.HeadCell>
                      <Table.HeadCell>Grade</Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {studentsQ.isLoading ? (
                        <Table.Row><Table.Cell colSpan={3}>Loading…</Table.Cell></Table.Row>
                      ) : (studentsQ.data?.results || []).length === 0 ? (
                        <Table.Row><Table.Cell colSpan={3} className="text-center text-slate-500">No students</Table.Cell></Table.Row>
                      ) : (
                        (studentsQ.data?.results || []).map((st: AdminStudentBrief) => (
                          <Table.Row key={st.id}>
                            <Table.Cell>{st.id}</Table.Cell>
                            <Table.Cell>{st.name}</Table.Cell>
                            <Table.Cell>{st.grade}</Table.Cell>
                          </Table.Row>
                        ))
                      )}
                    </Table.Body>
                  </Table>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm">Total: {studentsQ.data?.count ?? 0}</div>
                <div className="flex gap-2">
                  <Button color="light" size="xs" disabled={studPage <= 1} onClick={() => setStudPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <span className="px-2">Page {studPage}</span>
                  <Button color="light" size="xs" disabled={(studentsQ.data?.results?.length ?? 0) === 0} onClick={() => setStudPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            </>
          )}

          {/* Structure Tab: Grades & Subjects for Schools; Fields & Academic Years for Universities/Colleges */}
          {activeTab === 'structure' && selected && (
            (() => {
              const t = (selected.creator_type_name || '').toLowerCase();
              const isUni = t.includes('university') || t.includes('college');
              if (!isUni) {
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Grades</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 items-end">
                        <div className="md:col-span-2">
                          <Label value="New grade name" />
                          <TextInput value={newGrade} onChange={(e)=>setNewGrade(e.target.value)} placeholder="New grade name" />
                        </div>
                        <div>
                          <Button onClick={async()=>{ if (!newGrade.trim()) return; const g = await adminCreateGrade(selected.id, newGrade.trim()); setGrades(prev=>[...prev, g]); setNewGrade(''); }}>Add</Button>
                        </div>
                      </div>
                      <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark max-h-80 overflow-x-auto">
                        <div className="max-w-full overflow-x-auto">
                          <Table hoverable>
                            <Table.Head>
                              <Table.HeadCell>Name</Table.HeadCell>
                              <Table.HeadCell>Actions</Table.HeadCell>
                            </Table.Head>
                            <Table.Body className="divide-y">
                              {grades.map(g => (
                                <Table.Row key={g.id} className={selectedGradeId===g.id ? 'bg-gray-50 dark:bg-boxdark-2' : ''}>
                                  <Table.Cell onClick={async()=>{ setSelectedGradeId(g.id); const subs = await adminGetGradeSubjects(selected.id, g.id); setSubjects(subs); }} className="cursor-pointer">
                                    {g.id === editingGradeId ? (
                                      <TextInput value={editingGradeName} onChange={(e)=>setEditingGradeName(e.target.value)} />
                                    ) : (
                                      g.name
                                    )}
                                  </Table.Cell>
                                  <Table.Cell>
                                    {g.id === editingGradeId ? (
                                      <div className="flex gap-2">
                                        <Button color="success" size="xs" onClick={async()=>{ const up = await adminUpdateGrade(selected.id, g.id, editingGradeName.trim() || g.name); setGrades(prev => prev.map(x => x.id===g.id ? up : x)); setEditingGradeId(null); setEditingGradeName(''); }}>Save</Button>
                                        <Button color="light" size="xs" onClick={()=>{ setEditingGradeId(null); setEditingGradeName(''); }}>Cancel</Button>
                                      </div>
                                    ) : (
                                      <div className="flex gap-2">
                                        <Button color="light" size="xs" onClick={()=>{ setEditingGradeId(g.id); setEditingGradeName(g.name); }}>Rename</Button>
                                        <Button color="failure" size="xs" onClick={async()=>{ if (confirm('Delete grade and unlink subjects?')) { await adminDeleteGrade(selected.id, g.id); setGrades(prev=>prev.filter(x=>x.id!==g.id)); if (selectedGradeId===g.id) { setSelectedGradeId(null); setSubjects([]);} } }}>Delete</Button>
                                      </div>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                              {grades.length===0 && (
                                <Table.Row><Table.Cell colSpan={2}>No grades</Table.Cell></Table.Row>
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Subjects {selectedGradeId ? `(Grade #${selectedGradeId})` : ''}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 items-end">
                        <div className="md:col-span-2">
                          <Label value="New subject name" />
                          <TextInput value={newSubject} onChange={(e)=>setNewSubject(e.target.value)} placeholder="New subject name" disabled={!selectedGradeId} />
                        </div>
                        <div>
                          <Button disabled={!selectedGradeId} onClick={async()=>{ if (!selected || !selectedGradeId || !newSubject.trim()) return; const s = await adminCreateSubject(selected.id, selectedGradeId, newSubject.trim()); setSubjects(prev=>[...prev, s]); setNewSubject(''); }}>Add</Button>
                        </div>
                      </div>
                      <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark max-h-80 overflow-x-auto">
                        <div className="max-w-full overflow-x-auto">
                          <Table hoverable>
                            <Table.Head>
                              <Table.HeadCell>Name</Table.HeadCell>
                              <Table.HeadCell>Actions</Table.HeadCell>
                            </Table.Head>
                            <Table.Body className="divide-y">
                              {subjects.map(s => (
                                <Table.Row key={s.id}>
                                  <Table.Cell>
                                    {s.id === editingSubjectId ? (
                                      <TextInput value={editingSubjectName} onChange={(e)=>setEditingSubjectName(e.target.value)} />
                                    ) : s.name}
                                  </Table.Cell>
                                  <Table.Cell>
                                    {s.id === editingSubjectId ? (
                                      <div className="flex gap-2">
                                        <Button color="success" size="xs" onClick={async()=>{ if (!selected) return; const up = await adminUpdateSubject(selected.id, s.id, editingSubjectName.trim() || s.name); setSubjects(prev => prev.map(x => x.id===s.id ? up : x)); setEditingSubjectId(null); setEditingSubjectName(''); }}>Save</Button>
                                        <Button color="light" size="xs" onClick={()=>{ setEditingSubjectId(null); setEditingSubjectName(''); }}>Cancel</Button>
                                      </div>
                                    ) : (
                                      <div className="flex gap-2">
                                        <Button color="light" size="xs" onClick={()=>{ setEditingSubjectId(s.id); setEditingSubjectName(s.name); }}>Rename</Button>
                                        <Button color="failure" size="xs" onClick={async()=>{ if (!selected) return; if (confirm('Delete subject?')) { await adminDeleteSubject(selected.id, s.id); setSubjects(prev=>prev.filter(x=>x.id!==s.id)); } }}>Delete</Button>
                                      </div>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                              {subjects.length===0 && (
                                <Table.Row><Table.Cell colSpan={2}>{selectedGradeId ? 'No subjects' : 'Select a grade'}</Table.Cell></Table.Row>
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              // University/College view
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Fields of Study</h4>
                    <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark max-h-80 overflow-x-auto">
                      <div className="max-w-full overflow-x-auto">
                        <Table hoverable>
                          <Table.Head>
                            <Table.HeadCell>Name</Table.HeadCell>
                          </Table.Head>
                          <Table.Body className="divide-y">
                            {fields.map(f => (
                              <Table.Row key={f.id} className={selectedFieldId===f.id ? 'bg-gray-50 dark:bg-boxdark-2' : ''}>
                                <Table.Cell onClick={async()=>{ setSelectedFieldId(f.id); if (selected) { const ys = await adminGetFieldAcademicYears(selected.id, f.id); setYears(ys); } }} className="cursor-pointer">
                                  {f.name}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                            {fields.length===0 && (
                              <Table.Row><Table.Cell colSpan={1}>No fields</Table.Cell></Table.Row>
                            )}
                          </Table.Body>
                        </Table>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Academic Years {selectedFieldId ? `(Field #${selectedFieldId})` : ''}</h4>
                    <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark max-h-80 overflow-x-auto">
                      <div className="max-w-full overflow-x-auto">
                        <Table hoverable>
                          <Table.Head>
                            <Table.HeadCell>Name</Table.HeadCell>
                          </Table.Head>
                          <Table.Body className="divide-y">
                            {years.map(y => (
                              <Table.Row key={y.id}>
                                <Table.Cell>{y.name}</Table.Cell>
                              </Table.Row>
                            ))}
                            {years.length===0 && (
                              <Table.Row><Table.Cell colSpan={1}>{selectedFieldId ? 'No years' : 'Select a field'}</Table.Cell></Table.Row>
                            )}
                          </Table.Body>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          )}

          {/* Admin Users Tab */}
          {activeTab === 'admins' && selected && (
            <div className="space-y-4">
              <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark">
                <h4 className="font-semibold mb-3">Current Admin Users</h4>
                <div className="max-w-full overflow-x-auto">
                  <Table hoverable>
                    <Table.Head>
                      <Table.HeadCell>ID</Table.HeadCell>
                      <Table.HeadCell>Username</Table.HeadCell>
                      <Table.HeadCell>Name</Table.HeadCell>
                      <Table.HeadCell>Email</Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {admins.map((a) => (
                        <Table.Row key={a.id}>
                          <Table.Cell>{a.id}</Table.Cell>
                          <Table.Cell>{a.username}</Table.Cell>
                          <Table.Cell>{((a.first_name || '') + ' ' + (a.last_name || '')).trim()}</Table.Cell>
                          <Table.Cell>{a.email || ''}</Table.Cell>
                        </Table.Row>
                      ))}
                      {admins.length === 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={4} className="text-center text-slate-500">No admin account</Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </div>
              </div>

              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-800 text-sm">Assign an existing user or create a new admin and associate to this institute.</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="mb-1" value="Existing User ID" />
                  <TextInput value={adminForm.existing_user_id} onChange={(e)=>setAdminForm({...adminForm, existing_user_id: e.target.value})} placeholder="User ID or Username" />
                </div>
                <div className="md:col-span-2 flex items-end">
                  <Button onClick={async()=>{ if (!selected) return; if (!adminForm.existing_user_id.trim()) { alert('Please enter a username or ID.'); return; } await adminAssignSchoolAdmin(selected.id, { username: adminForm.existing_user_id.trim() }); alert('Assigned'); setAdminForm({...adminForm, existing_user_id: ''}); try { const list = await adminGetSchoolAdmins(selected.id); setAdmins(list); } catch {} }}>Assign Existing</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="mb-1" value="Username" />
                  <TextInput value={adminForm.username} onChange={(e)=>setAdminForm({...adminForm, username: e.target.value})} />
                </div>
                <div>
                  <Label className="mb-1" value="Password" />
                  <TextInput type="password" value={adminForm.password} onChange={(e)=>setAdminForm({...adminForm, password: e.target.value})} />
                </div>
                <div>
                  <Label className="mb-1" value="Email" />
                  <TextInput value={adminForm.email} onChange={(e)=>setAdminForm({...adminForm, email: e.target.value})} />
                </div>
                <div>
                  <Label className="mb-1" value="First Name" />
                  <TextInput value={adminForm.first_name} onChange={(e)=>setAdminForm({...adminForm, first_name: e.target.value})} />
                </div>
                <div>
                  <Label className="mb-1" value="Last Name" />
                  <TextInput value={adminForm.last_name} onChange={(e)=>setAdminForm({...adminForm, last_name: e.target.value})} />
                </div>
                <div className="md:col-span-1 flex items-end">
                  <Button onClick={async()=>{ if (!selected) return; if (!adminForm.username.trim()) return; await adminAssignSchoolAdmin(selected.id, { username: adminForm.username.trim(), password: adminForm.password || undefined, email: adminForm.email || undefined, first_name: adminForm.first_name || undefined, last_name: adminForm.last_name || undefined }); alert('Admin created & assigned'); setAdminForm({ existing_user_id: '', username: '', password: '', email: '', first_name: '', last_name: '' }); try { const list = await adminGetSchoolAdmins(selected.id); setAdmins(list); } catch {} }}>Create & Assign</Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit dialog */}
      <Modal show={editOpen && !!editing} onClose={() => { setEditOpen(false); setEditing(null); }} className="z-[2000]">
        <Modal.Header>Edit School: {editing?.name}</Modal.Header>
        <Modal.Body>
          <form onSubmit={submitEdit} className="space-y-3">
            <div>
              <Label className="mb-1" value="Name" />
              <TextInput id="edit-name" defaultValue={editing?.name || ''} />
            </div>
            <div>
              <Label className="mb-1" value="Type" />
              <Select id="edit-creator" defaultValue={editing ? String(editing.creator_type) : ''}>
                {types.map((t: AdminCreatorType) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button color="light" type="button" onClick={() => { setEditOpen(false); setEditing(null); }}>Cancel</Button>
              <Button type="submit" disabled={updateMut.isPending}>{updateMut.isPending ? 'Saving…' : 'Save'}</Button>
            </div>
            {updateMut.isError && <div className="text-sm text-red-600">Failed: {(updateMut.error as any)?.response?.data ? JSON.stringify((updateMut.error as any).response.data) : (updateMut.error as any)?.message}</div>}
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminSchools;
